import { useEffect, useRef, useState } from 'react';
import { ArrowRight, HelpCircle } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '@vnetwork/ui/primitives';
import { brand } from '@/config/brand';
import styles from './HelpTooltip.module.css';

/**
 * Hover-opened light popover carrying a product's name, one-line description and
 * an optional docs link. Shared by the Overview dashboard and the All-services
 * catalog so both read identically (a plain "?" glyph → white surface card).
 *
 * The open state is held locally with a short close delay so the cursor can
 * cross the trigger→content gap (sideOffset) to click "Read the docs" — closing
 * immediately on the trigger's mouseleave would swallow the pointer in that gap.
 */
export function HelpTooltip({
  title,
  description,
  docsHref,
}: {
  title: string;
  description: string;
  docsHref?: string;
}) {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<number | null>(null);
  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const openNow = () => {
    cancelClose();
    setOpen(true);
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = window.setTimeout(() => setOpen(false), 120);
  };
  useEffect(() => cancelClose, []);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={styles.helpTrigger}
          aria-label={`About ${title}`}
          onMouseEnter={openNow}
          onMouseLeave={scheduleClose}
        >
          <HelpCircle size={14} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="start"
        sideOffset={8}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onMouseEnter={openNow}
        onMouseLeave={scheduleClose}
      >
        <div className={styles.helpPopover}>
          <span className={styles.helpPopoverTitle}>{title}</span>
          <span style={{ display: 'block' }}>{description}</span>
          {docsHref && brand.features.documentation && (
            <a href={docsHref} target="_blank" rel="noreferrer" className={styles.helpPopoverLink}>
              Read the docs <ArrowRight size={12} />
            </a>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
